import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Package,
  CreditCard,
  Users,
  Calendar,
  Download,
  Filter
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  reportsService,
  ReportSummary,
  SalesByPeriod,
  SalesByProduct,
  SalesByCategory,
  SalesByPaymentMethod,
  SalesByUser
} from '../services/reportsService';
import './Reports.css';

type ReportType =
  | 'period'
  | 'product'
  | 'category'
  | 'payment-method'
  | 'user';
type PeriodType = 'daily' | 'weekly' | 'monthly' | 'yearly';

// Chart colors
const COLORS = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe', '#fbbf24', '#f59e0b'];

const Reports: React.FC = () => {
  const [activeReport, setActiveReport] = useState<ReportType>('period');
  const [period, setPeriod] = useState<PeriodType>('daily');
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  // Data states
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [periodData, setPeriodData] = useState<SalesByPeriod[]>([]);
  const [productData, setProductData] = useState<SalesByProduct[]>([]);
  const [categoryData, setCategoryData] = useState<SalesByCategory[]>([]);
  const [paymentData, setPaymentData] = useState<SalesByPaymentMethod[]>([]);
  const [userData, setUserData] = useState<SalesByUser[]>([]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await reportsService.getSummary(startDate, endDate);
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  }, [startDate, endDate]);

  const loadReportData = useCallback(async () => {
    setLoading(true);
    try {
      switch (activeReport) {
        case 'period':
          const periodResult = await reportsService.getSalesByPeriod(
            period,
            startDate,
            endDate
          );
          setPeriodData(periodResult);
          break;
        case 'product':
          const productResult = await reportsService.getSalesByProduct(
            startDate,
            endDate
          );
          setProductData(productResult);
          break;
        case 'category':
          const categoryResult = await reportsService.getSalesByCategory(
            startDate,
            endDate
          );
          setCategoryData(categoryResult);
          break;
        case 'payment-method':
          const paymentResult = await reportsService.getSalesByPaymentMethod(
            startDate,
            endDate
          );
          setPaymentData(paymentResult);
          break;
        case 'user':
          const userResult = await reportsService.getSalesByUser(
            startDate,
            endDate
          );
          setUserData(userResult);
          break;
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeReport, period, startDate, endDate]);

  useEffect(() => {
    loadSummary();
    loadReportData();
  }, [loadSummary, loadReportData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      switch (activeReport) {
        case 'period':
          await reportsService.exportSalesByPeriod(period, startDate, endDate);
          break;
        case 'product':
          await reportsService.exportSalesByProduct(startDate, endDate);
          break;
        case 'category':
          await reportsService.exportSalesByCategory(startDate, endDate);
          break;
        case 'payment-method':
          await reportsService.exportSalesByPaymentMethod(startDate, endDate);
          break;
        case 'user':
          await reportsService.exportSalesByUser(startDate, endDate);
          break;
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="reports-container">
      {/* Summary Cards */}
      {summary && (
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-icon revenue">
              <DollarSign size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Ingresos Totales</p>
              <h3 className="summary-value">{formatCurrency(summary.totalRevenue)}</h3>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon orders">
              <ShoppingCart size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Total Órdenes</p>
              <h3 className="summary-value">{summary.totalOrders}</h3>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon products">
              <Package size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Productos Vendidos</p>
              <h3 className="summary-value">{summary.totalProducts}</h3>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon profit">
              <TrendingUp size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Ganancia</p>
              <h3 className="summary-value">{formatCurrency(summary.totalProfit)}</h3>
              <p className="summary-subtitle">{summary.profitMargin.toFixed(1)}% margen</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section card">
        <div className="filters-header">
          <h3>
            <Filter size={20} />
            Filtros
          </h3>
          <button 
            className="export-btn" 
            onClick={handleExport}
            disabled={exporting}
          >
            <Download size={18} />
            {exporting ? 'Exportando...' : 'Exportar a Excel'}
          </button>
        </div>
        <div className="filters-content">
          <div className="filter-group">
            <label>Fecha Inicio</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Fecha Fin</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {activeReport === 'period' && (
            <div className="filter-group">
              <label>Período</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value as PeriodType)}>
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensual</option>
                <option value="yearly">Anual</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Report Tabs */}
      <div className="report-tabs">
        <button
          className={`report-tab ${activeReport === 'period' ? 'active' : ''}`}
          onClick={() => setActiveReport('period')}
        >
          <Calendar size={18} />
          Por Período
        </button>
        <button
          className={`report-tab ${activeReport === 'product' ? 'active' : ''}`}
          onClick={() => setActiveReport('product')}
        >
          <Package size={18} />
          Por Producto
        </button>
        <button
          className={`report-tab ${activeReport === 'category' ? 'active' : ''}`}
          onClick={() => setActiveReport('category')}
        >
          <BarChart3 size={18} />
          Por Categoría
        </button>
        <button
          className={`report-tab ${activeReport === 'payment-method' ? 'active' : ''}`}
          onClick={() => setActiveReport('payment-method')}
        >
          <CreditCard size={18} />
          Por Método de Pago
        </button>
        <button
          className={`report-tab ${activeReport === 'user' ? 'active' : ''}`}
          onClick={() => setActiveReport('user')}
        >
          <Users size={18} />
          Por Usuario
        </button>
      </div>

      {/* Report Content */}
      <div className="report-content card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando datos...</p>
          </div>
        ) : (
          <>
            {/* View Mode Tabs */}
            <div className="view-mode-tabs">
              <button
                className={`view-tab ${viewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setViewMode('chart')}
              >
                <BarChart3 size={18} />
                Gráfica
              </button>
              <button
                className={`view-tab ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <Package size={18} />
                Tabla
              </button>
            </div>

            {activeReport === 'period' && (
              <>
                {viewMode === 'chart' && (
                  <div className="chart-container">
                    <h3>Tendencia de Ventas</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={periodData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => formatDate(date)}
                        />
                        <YAxis />
                        <Tooltip 
                          formatter={(value: number) => formatCurrency(value)}
                          labelFormatter={(date) => formatDate(date)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="totalAmount" 
                          stroke={COLORS[0]} 
                          strokeWidth={2}
                          name="Ingresos"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Ventas</th>
                          <th>Órdenes</th>
                          <th>Productos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodData.map((row, index) => (
                          <tr key={index}>
                            <td>{formatDate(row.date)}</td>
                            <td className="currency">{formatCurrency(row.totalAmount)}</td>
                            <td>{row.orderCount}</td>
                            <td>{row.productsSold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {activeReport === 'product' && (
              <>
                {viewMode === 'chart' && (
                  <div className="chart-container">
                    <h3>Top 10 Productos</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={productData.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="totalRevenue" fill={COLORS[0]} name="Ingresos" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Categoría</th>
                        <th>Cantidad</th>
                        <th>Precio Prom.</th>
                        <th>Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.productName}</td>
                          <td>{row.category}</td>
                          <td>{row.quantitySold}</td>
                          <td className="currency">{formatCurrency(row.averagePrice)}</td>
                          <td className="currency">{formatCurrency(row.totalRevenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            )}

            {activeReport === 'category' && (
              <>
                {viewMode === 'chart' && (
                  <div className="chart-container">
                    <h3>Distribución por Categoría</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          dataKey="totalRevenue"
                          nameKey="categoryName"
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          label={(entry) => `${entry.categoryName}: ${entry.percentage.toFixed(1)}%`}
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Categoría</th>
                        <th>Productos</th>
                        <th>Cantidad</th>
                        <th>Ingresos</th>
                        <th>Porcentaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.categoryName}</td>
                          <td>{row.productCount}</td>
                          <td>{row.quantitySold}</td>
                          <td className="currency">{formatCurrency(row.totalRevenue)}</td>
                          <td>
                            <div className="percentage-bar">
                              <div
                                className="percentage-fill"
                                style={{ width: `${row.percentage}%` }}
                              ></div>
                              <span>{row.percentage.toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            )}

            {activeReport === 'payment-method' && (
              <>
                {viewMode === 'chart' && (
                  <div className="chart-container">
                    <h3>Métodos de Pago</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={paymentData}
                          dataKey="totalAmount"
                          nameKey="paymentMethodName"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={(entry) => `${entry.paymentMethodName}: ${entry.percentage.toFixed(1)}%`}
                        >
                          {paymentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Método de Pago</th>
                        <th>Transacciones</th>
                        <th>Monto Total</th>
                        <th>Porcentaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.paymentMethodName}</td>
                          <td>{row.transactionCount}</td>
                          <td className="currency">{formatCurrency(row.totalAmount)}</td>
                          <td>
                            <div className="percentage-bar">
                              <div
                                className="percentage-fill"
                                style={{ width: `${row.percentage}%` }}
                              ></div>
                              <span>{row.percentage.toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            )}

            {activeReport === 'user' && (
              <>
                {viewMode === 'chart' && (
                  <div className="chart-container">
                    <h3>Rendimiento por Usuario</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={userData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="username" />
                        <YAxis />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="totalRevenue" fill={COLORS[0]} name="Ingresos" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th>Rol</th>
                        <th>Ventas</th>
                        <th>Ingresos</th>
                        <th>Ticket Prom.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.username}</td>
                          <td>
                            <span className="role-badge">{row.role}</span>
                          </td>
                          <td>{row.totalSales}</td>
                          <td className="currency">{formatCurrency(row.totalRevenue)}</td>
                          <td className="currency">{formatCurrency(row.averageTicket)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
